import { computed, ref } from 'vue';

// Groups an ordered list of form field definitions into "pages" (steps) for
// the step-by-step (Typeform-style) layout. By default every field gets its
// own step (one-field-per-step), but a field can opt into sharing the
// previous field's step via `field.group_with_previous === true` — a
// consecutive run of such fields collapses into a single multi-field step.
// The very first field in the array always starts step 1 regardless of its
// own `group_with_previous` value (there is no "previous" step for it to
// join). A `consent` field, wherever it sits in the array, is excluded from
// this grouping and instead pulled out and appended to the last step, so it
// always shares the final screen with the submit button rather than getting
// a step (grouped or not) of its own.
export const buildSteps = (fields = []) => {
  if (!fields.length) return [];

  const consentIndex = fields.findIndex(field => field.type === 'consent');
  const contentFields =
    consentIndex === -1 ? fields : fields.filter((_, i) => i !== consentIndex);

  const steps = [];
  contentFields.forEach((field, index) => {
    const startsNewStep = index === 0 || field.group_with_previous !== true;
    if (startsNewStep || !steps.length) {
      steps.push([field]);
    } else {
      steps[steps.length - 1].push(field);
    }
  });
  if (!steps.length) steps.push([]);

  if (consentIndex !== -1) {
    const lastStep = steps[steps.length - 1];
    steps[steps.length - 1] = [...lastStep, fields[consentIndex]];
  }

  return steps;
};

// Drives the current-step index, progress fraction and next/back navigation
// for the multi-step form layout. Framework-agnostic beyond Vue's reactivity
// primitives, so it can be unit tested without mounting any component, and
// shared as-is between the public hosted page (app/javascript/forms/App.vue)
// and the builder's live preview (FormLivePreview.vue) — one source of truth
// for step math so the preview never drifts from the real hosted behavior.
//
// @param {import('vue').Ref<Array>} fields - reactive list of form field defs
// @param {(stepFields: Array) => boolean} [canAdvance] - validation gate run
//   against the fields of the step being left; returning false blocks the
//   move to the next step. Omit when there is nothing to validate (e.g. a
//   read-only preview).
export function useFormSteps({ fields, canAdvance }) {
  const stepIndex = ref(0);

  const steps = computed(() => buildSteps(fields.value || []));
  const totalSteps = computed(() => steps.value.length);

  // Clamp against the raw index so a step list that shrinks (e.g. a field
  // removed live in the builder) never leaves the cursor pointing past the
  // end — it settles on the new last step instead of rendering nothing.
  const currentIndex = computed(() => {
    if (totalSteps.value === 0) return 0;
    return Math.min(Math.max(stepIndex.value, 0), totalSteps.value - 1);
  });

  const currentStepFields = computed(
    () => steps.value[currentIndex.value] || []
  );
  const isFirstStep = computed(() => currentIndex.value <= 0);
  const isLastStep = computed(() => currentIndex.value >= totalSteps.value - 1);
  const progress = computed(() =>
    totalSteps.value ? (currentIndex.value + 1) / totalSteps.value : 0
  );

  const goNext = () => {
    if (isLastStep.value) return false;
    if (canAdvance && !canAdvance(currentStepFields.value)) return false;
    stepIndex.value = currentIndex.value + 1;
    return true;
  };

  const goPrev = () => {
    if (isFirstStep.value) return false;
    stepIndex.value = currentIndex.value - 1;
    return true;
  };

  const reset = () => {
    stepIndex.value = 0;
  };

  return {
    currentIndex,
    steps,
    totalSteps,
    currentStepFields,
    isFirstStep,
    isLastStep,
    progress,
    goNext,
    goPrev,
    reset,
  };
}
